import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Modal, Select, Spin, message } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useConfig } from '../../../hooks/useConfig';
import { cy } from './DiagramCanvas';
import { ACTIONS, currentDiagram } from './App';
import { applyAutoLabelWidth } from '../utils/node-label-size';
import { computeDisplayLabel } from '../controller/general';
import { updateFromMasterModel } from '../controller/diagram-switching';
import { EdgeType } from '../model/edge-model';
import { addNode } from '../controller/general';
import { DiagramTreeNode, diagramTreeRoot } from '../model/diagram-tree-model';
import {
  ChildGroupType,
  MMNode,
  MMRoot,
  NodeType,
  masterModelRoot,
} from '../model/node-model';
import { MMEdge } from '../model/edge-model';
import { OPMApi } from '../../../api/opmApi';
import type { ProcessFuncCodeDetailDto } from '../../../api/opmApi';
import ObjectNodePropertyPanel from './ObjectNodePropertyPanel';
import ProcessNodePropertyPanel from './ProcessNodePropertyPanel';
import {
  isPackageNameValid,
  isTopLevelNode,
  normalizePackageName,
  recalculateNamespacesForMasterModel,
} from '../utils/node-namespace';

const MAX_TAG_LENGTH = 5;
const MAX_TAGS = 3;
const MIN_NAME_LENGTH = 2;
const MAX_NAME_LENGTH = 20;
const MIN_CODE_NAME_LENGTH = 2;
const MAX_DESCRIPTION_LENGTH = 200;
const MAX_CODE_NAME_LENGTH = 200;
// 产品规则：名称长度 2-20，英文名称长度 2-200 且必须以英文字母开头；名称不能以数字开头。
const NAME_VALID_PATTERN = /^[a-zA-Z\u4e00-\u9fa5][a-zA-Z\u4e00-\u9fa50-9_]*$/;
const CODE_NAME_VALID_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

interface DuplicateLabelCandidate {
  id: string;
  label: string;
  codeName: string;
  diagramLabels: string[];
  mmRef: MMNode;
}

const isVisibleDiagramNodeData = (data: any): boolean => {
  if (!data) return false;
  if (data.isMarkerLabel) return false;
  if (data.display === 'none') return false;
  if (data.MMRef?.deleted) return false;
  return true;
};

/**
 * 跨全部图层检查名称是否重名（排除当前节点自身与已删除节点）。
 */
const hasDuplicateLabelAcrossLayers = (
  label: string,
  currentNodeId?: string | null
): boolean => {
  const normalized = (label || '').trim();
  if (!normalized) return false;

  let duplicated = false;
  const walk = (node: MMNode) => {
    if (!node || duplicated) return;
    if (!node.deleted) {
      const sameLabel = (node.label || '').trim() === normalized;
      const isCurrent =
        !!currentNodeId && String(node.id) === String(currentNodeId);
      if (sameLabel && !isCurrent) {
        duplicated = true;
        return;
      }
    }
    (node.children || []).forEach(child => walk(child));
  };

  masterModelRoot.children.forEach(child => walk(child));
  return duplicated;
};

/**
 * 跨全部图层检查英文名称是否重名（排除当前节点自身与已删除节点）。
 */
const hasDuplicateCodeNameAcrossLayers = (
  codeName: string,
  currentNodeId?: string | null
): boolean => {
  const normalized = (codeName || '').trim();
  if (!normalized) return false;

  let duplicated = false;
  const walk = (node: MMNode) => {
    if (!node || duplicated) return;
    if (!node.deleted) {
      const sameCodeName = (node.codeName || '').trim() === normalized;
      const isCurrent =
        !!currentNodeId && String(node.id) === String(currentNodeId);
      if (sameCodeName && !isCurrent) {
        duplicated = true;
        return;
      }
    }
    (node.children || []).forEach(child => walk(child));
  };
  masterModelRoot.children.forEach(child => walk(child));
  return duplicated;
};

/**
 * 获取同名候选节点（跨全部图层，排除当前节点）。
 */
const findDuplicateLabelCandidates = (
  label: string,
  currentNodeId?: string | null
): MMNode[] => {
  const normalized = (label || '').trim();
  if (!normalized) return [];

  const result: MMNode[] = [];
  const walk = (node: MMNode) => {
    if (!node) return;
    if (!node.deleted) {
      const sameLabel = (node.label || '').trim() === normalized;
      const isCurrent =
        !!currentNodeId && String(node.id) === String(currentNodeId);
      if (sameLabel && !isCurrent) {
        result.push(node);
      }
    }
    (node.children || []).forEach(child => walk(child));
  };
  masterModelRoot.children.forEach(child => walk(child));
  return result;
};

/**
 * 统计节点在多少个图层中出现。
 */
const countNodeDiagramAppearances = (nodeId: string): number => {
  if (!nodeId) return 0;
  let count = 0;
  const walk = (diagram: DiagramTreeNode) => {
    if (!diagram) return;
    if (diagram === currentDiagram) {
      const currentNode = cy?.getElementById(nodeId);
      if (
        currentNode &&
        currentNode.length > 0 &&
        currentNode.data('display') !== 'none' &&
        !currentNode.data('isMarkerLabel') &&
        !currentNode.data('MMRef')?.deleted
      ) {
        count += 1;
      }
      (diagram.children || []).forEach(child => walk(child));
      return;
    }
    const nodes = diagram.diagramJson?.elements?.nodes || [];
    const hit = nodes.some((item: any) => {
      const data = item?.data || {};
      if (!isVisibleDiagramNodeData(data)) return false;
      const id = data.id || data.MMRefId || data.MMRef?.id;
      return String(id) === String(nodeId);
    });
    if (hit) count += 1;
    (diagram.children || []).forEach(child => walk(child));
  };
  walk(diagramTreeRoot);
  return count;
};

/**
 * 获取节点所在图层标签列表。
 */
const getNodeDiagramLabels = (nodeId: string): string[] => {
  if (!nodeId) return [];
  const labels: string[] = [];
  const walk = (diagram: DiagramTreeNode) => {
    if (!diagram) return;
    if (diagram === currentDiagram) {
      const currentNode = cy?.getElementById(nodeId);
      if (
        currentNode &&
        currentNode.length > 0 &&
        currentNode.data('display') !== 'none' &&
        !currentNode.data('isMarkerLabel') &&
        !currentNode.data('MMRef')?.deleted
      ) {
        labels.push(String(diagram.label || '未命名图层'));
      }
      (diagram.children || []).forEach(child => walk(child));
      return;
    }
    const nodes = diagram.diagramJson?.elements?.nodes || [];
    const hit = nodes.some((item: any) => {
      const data = item?.data || {};
      if (!isVisibleDiagramNodeData(data)) return false;
      const id = data.id || data.MMRefId || data.MMRef?.id;
      return String(id) === String(nodeId);
    });
    if (hit) {
      labels.push(String(diagram.label || '未命名图层'));
    }
    (diagram.children || []).forEach(child => walk(child));
  };
  walk(diagramTreeRoot);
  return Array.from(new Set(labels));
};

/**
 * 获取当前图层中的节点 ID 集合（排除 marker label）。
 */
const getCurrentDiagramNodeIdSet = (): Set<string> => {
  const result = new Set<string>();
  if (!cy) return result;
  cy.nodes().forEach(node => {
    if (node.data('isMarkerLabel')) return;
    if (node.data('display') === 'none') return;
    if (node.data('MMRef')?.deleted) return;
    result.add(String(node.id()));
  });
  return result;
};

interface NodePropertySidebarProps {
  visible: boolean;
  selectedNodeId: string | null;
  onClose: () => void;
  dispatch: Function;
  onOpenProcessDefinition: (nodeId: string) => void | Promise<void>;
  offsetRight?: number;
}

const FieldBlock: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const ReadonlyTextValue: React.FC<{
  value: string;
}> = ({ value }) => {
  return <div className="text-sm text-gray-700 break-all">{value || '-'}</div>;
};

const DEFAULT_PACKAGE_NAME_PLACEHOLDER = '请输入包名，如 com.example';
const DISABLED_PACKAGE_NAME_PLACEHOLDER = '继承上级包名，不可单独设置';
const DISABLED_PACKAGE_NAME_TITLE = '当前节点继承上级包名，不可单独设置';

const SectionBlock: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <section className="flex flex-col gap-4">
      <div className="text-sm font-semibold text-gray-800">{title}</div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
};

/**
 * 统一格式化运行时限制文案，便于属性面板与定义弹窗保持一致。
 */
const formatRuntimeLimitText = (
  value: number | null | undefined,
  unit: string
) => {
  if (value === null || value === undefined) return '-';
  if (value === -1) return `不限制`;
  return `${value}${unit}`;
};

const NodePropertySidebar: React.FC<NodePropertySidebarProps> = ({
  visible,
  selectedNodeId,
  onClose,
  dispatch,
  onOpenProcessDefinition,
  offsetRight = 0,
}) => {
  const { globalConfig } = useConfig();
  const [searchParams] = useSearchParams();
  const width = 260;
  const enableProcessNodeCascade = !!(globalConfig as any)
    .enableProcessNodeCascade;
  const ontologyIdParam = searchParams.get('id');
  const ontologyId = ontologyIdParam ? parseInt(ontologyIdParam, 10) : 1;
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [codeName, setCodeName] = useState('');
  const [codeNameError, setCodeNameError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [namespace, setNamespace] = useState('');
  const [packageName, setPackageName] = useState('');
  const [packageNameError, setPackageNameError] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [exampleValues, setExampleValues] = useState('');
  const [autonomyAction, setAutonomyAction] = useState('');
  const [autonomyPrompt, setAutonomyPrompt] = useState('');
  const [autonomyWithTool, setAutonomyWithTool] = useState(false);
  const [processDefinition, setProcessDefinition] =
    useState<ProcessFuncCodeDetailDto | null>(null);
  const [loadingProcessDefinition, setLoadingProcessDefinition] =
    useState(false);
  const [associateModalVisible, setAssociateModalVisible] = useState(false);
  const [associateCandidates, setAssociateCandidates] = useState<
    DuplicateLabelCandidate[]
  >([]);
  const [selectedAssociateId, setSelectedAssociateId] = useState('');
  const editingNodeIdRef = useRef<string | null>(null);
  const nameInputRef = useRef<any>(null);
  const codeNameInputRef = useRef<any>(null);
  const descriptionInputRef = useRef<any>(null);
  const packageNameInputRef = useRef<any>(null);

  const currentNode = useMemo(() => {
    if (!cy || !selectedNodeId) return null;
    const node = cy.getElementById(selectedNodeId);
    return node && node.length > 0 ? node : null;
  }, [selectedNodeId]);

  const nodeType = (currentNode?.data('type') ||
    currentNode?.data('MMRef')?.type ||
    '') as string;
  const isObjectNode = nodeType === 'object';
  const isProcessNode = nodeType === 'process';
  const isStateOrValueNode = nodeType === 'state' || nodeType === 'value';
  const currentMMRef = currentNode?.data('MMRef');
  const isTopLevelPackageEditable =
    currentMMRef instanceof MMNode ? isTopLevelNode(currentMMRef) : false;
  const nodeIdText = useMemo(() => {
    if (!currentNode) return '';
    const mmRef = currentNode.data('MMRef');
    return mmRef?.id?.toString?.() || currentNode.id() || '';
  }, [currentNode]);
  const parentNodeIdText = useMemo(() => {
    if (!(currentMMRef instanceof MMNode)) return '';
    const parent = currentMMRef.parent;
    return parent instanceof MMNode ? String(parent.id) : '';
  }, [currentMMRef]);
  const processNodeId = useMemo(() => {
    if (!currentNode) return '';
    const mmRef = currentNode.data('MMRef');
    return mmRef?.id?.toString?.() || currentNode.id() || '';
  }, [currentNode]);

  const restoreSidebarStateFromNode = (node: any) => {
    if (node) {
      const mmRef = node.data('MMRef');
      setNameError(null);
      setCodeNameError(null);
      setPackageNameError(null);
      setName(node.data('label') || node.data('name') || '');
      setCodeName(mmRef?.codeName || node.data('codeName') || '');
      setDescription(mmRef?.description || node.data('description') || '');
      setNamespace(mmRef?.namespace || node.data('namespace') || '');
      setPackageName(mmRef?.packageName || node.data('packageName') || '');
      setTags(Array.isArray(mmRef?.tags) ? mmRef?.tags : []);
      setTagInput('');
      setExampleValues(mmRef?.exampleValues || '');
      setAutonomyAction(mmRef?.autonomy?.action || '');
      setAutonomyPrompt(mmRef?.autonomy?.prompt || '');
      setAutonomyWithTool(!!mmRef?.autonomy?.withTool);
      return;
    }

    setNameError(null);
    setCodeNameError(null);
    setPackageNameError(null);
    setName('');
    setCodeName('');
    setDescription('');
    setNamespace('');
    setPackageName('');
    setTags([]);
    setTagInput('');
    setExampleValues('');
    setAutonomyAction('');
    setAutonomyPrompt('');
    setAutonomyWithTool(false);
  };

  useEffect(() => {
    restoreSidebarStateFromNode(currentNode);
  }, [currentNode]);

  useEffect(() => {
    if (!visible) {
      (window as any).__opmcvNodeEditorBlockClearSelection = false;
    }
    return () => {
      (window as any).__opmcvNodeEditorBlockClearSelection = false;
    };
  }, [visible]);

  // 点击节点后不再由侧边栏强制居中，避免高频跳动；
  // 视图可见性由 App.tsx 中 ensureSelectedVisible 统一按需处理。

  useEffect(() => {
    if (!visible || !isProcessNode || !processNodeId || !ontologyId) {
      setProcessDefinition(null);
      setLoadingProcessDefinition(false);
      return;
    }

    let cancelled = false;
    setLoadingProcessDefinition(true);
    // 过程类型与运行时配置现在统一从 function 接口读取，避免继续使用旧 definition 的过时代码/字段。
    OPMApi.getProcessFunctionDefinition(ontologyId, processNodeId)
      .then(definition => {
        if (!cancelled) {
          setProcessDefinition(definition);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProcessDefinition(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProcessDefinition(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [visible, isProcessNode, ontologyId, processNodeId]);

  const buildNodeSnapshot = (mmRef: any) => ({
    label: mmRef?.label || '',
    codeName: mmRef?.codeName || '',
    description: mmRef?.description || '',
    tags: Array.isArray(mmRef?.tags) ? [...mmRef.tags] : [],
    objectType: mmRef?.objectType || '',
    exampleValues: mmRef?.exampleValues || '',
    packageName: mmRef?.packageName || '',
    autonomy:
      mmRef?.type === NodeType.Process
        ? {
            action: mmRef?.autonomy?.action || '',
            prompt: mmRef?.autonomy?.prompt || '',
            withTool: !!mmRef?.autonomy?.withTool,
          }
        : undefined,
  });

  const ensureNodeSnapshot = (mmRef: any) => {
    if (!mmRef) return;
    if (!mmRef.__opmcvEditSnapshot) {
      mmRef.__opmcvEditSnapshot = buildNodeSnapshot(mmRef);
    }
  };

  const markNodeDirty = (mmRef: any) => {
    if (!mmRef) return;
    mmRef.__opmcvDirty = true;
  };

  const getActiveNode = () => {
    const nodeId = editingNodeIdRef.current || currentNode?.id();
    if (!cy || !nodeId) return null;
    const node = cy.getElementById(nodeId);
    if (!node || node.length === 0) return null;
    return node;
  };

  const closeAssociateModal = () => {
    setAssociateModalVisible(false);
    setAssociateCandidates([]);
    setSelectedAssociateId('');
  };

  const handleCancelAssociateModal = () => {
    const activeNode = getActiveNode();
    const fallbackName =
      activeNode?.data('label') || activeNode?.data('name') || '';
    setName(fallbackName);
    setNameError(null);
    (window as any).__opmcvNodeEditorBlockClearSelection = false;
    closeAssociateModal();
  };

  const handleSidebarClose = () => {
    const activeNode = getActiveNode() || currentNode;
    restoreSidebarStateFromNode(activeNode);
    editingNodeIdRef.current = null;
    (window as any).__opmcvNodeEditorBlockClearSelection = false;
    closeAssociateModal();
    onClose();
  };

  const handleAssociateNode = () => {
    if (!cy || !selectedAssociateId) {
      message.warning('请选择要关联的节点');
      return;
    }
    const activeNode = getActiveNode();
    if (!activeNode) {
      closeAssociateModal();
      return;
    }

    const sourceMMRef = activeNode.data('MMRef') as MMNode | null;
    if (!sourceMMRef) {
      closeAssociateModal();
      return;
    }

    const sourceChildren = (sourceMMRef.children || []).filter(
      child => !child.deleted
    );
    const nonStateValueChildren = sourceChildren.filter(
      child => child.type !== NodeType.State && child.type !== NodeType.Value
    );
    // 仅支持携带 state/value 子节点的关联；对象/过程子节点仍保持原限制，避免破坏 inzoom 结构。
    if (nonStateValueChildren.length > 0) {
      message.warning('当前节点存在对象/过程子节点，暂不支持直接关联');
      return;
    }
    if (sourceMMRef.diagram) {
      message.warning('当前节点已绑定子图，暂不支持直接关联');
      return;
    }

    // 保护：多图层实例节点不允许在属性面板直接切换关联。
    const sourceAppearances = Math.max(
      countNodeDiagramAppearances(String(sourceMMRef.id)),
      activeNode && activeNode.length > 0 ? 1 : 0
    );
    if (sourceAppearances > 1) {
      message.warning(
        '当前节点已在多个图层出现，暂不支持直接关联，请先清理其他图层实例'
      );
      return;
    }

    const candidate = associateCandidates.find(
      item => item.id === selectedAssociateId
    );
    if (!candidate) {
      message.warning('未找到可关联节点');
      return;
    }
    const targetMMRef = candidate.mmRef;

    if (targetMMRef.type !== sourceMMRef.type) {
      message.warning('仅支持关联相同类型的节点');
      return;
    }

    const sourceNodeId = activeNode.id();
    const targetNodeId = String(targetMMRef.id);
    const connectedEdges = activeNode.connectedEdges().toArray();
    const oldPosition = activeNode.position();
    const oldParentId = activeNode.data('parent') || '';
    const oldParentMMRef = sourceMMRef.parent as MMNode | MMRoot | null;

    let linkedNode = cy.getElementById(targetMMRef.id);
    if (linkedNode.length > 0) {
      linkedNode.data({ display: 'element' });
    } else {
      const parentMMRef = oldParentId
        ? (cy.getElementById(oldParentId).data('MMRef') as MMNode) ||
          oldParentMMRef
        : oldParentMMRef || masterModelRoot;

      addNode(
        cy,
        {
          id: targetMMRef.id,
          label: targetMMRef.label,
          type: targetMMRef.type as NodeType,
          parent: oldParentId,
          MMRef: targetMMRef,
        },
        {
          x: oldPosition.x,
          y: oldPosition.y,
        },
        parentMMRef as MMNode | MMRoot
      );
      linkedNode = cy.getElementById(targetMMRef.id);
    }

    if (linkedNode.length > 0) {
      if (oldParentId && linkedNode.data('parent') !== oldParentId) {
        linkedNode.move({ parent: oldParentId });
      }
      linkedNode.data({
        display: 'element',
        codeName: targetMMRef.codeName || '',
        childGroupType: targetMMRef.childGroupType || ChildGroupType.None,
      });
      linkedNode.position({ x: oldPosition.x, y: oldPosition.y });
    }

    // 复用关联时，同步 state/value 子节点级联关系：
    // - master model: 子节点从源父节点迁移到目标父节点；
    // - 当前图层: 子节点 cytoscape parent 改挂到目标父节点。
    const sourceStateValueChildren = sourceChildren.filter(
      child => child.type === NodeType.State || child.type === NodeType.Value
    );
    sourceStateValueChildren.forEach(childMMRef => {
      sourceMMRef.removeChild(childMMRef);

      const alreadyInTarget = (targetMMRef.children || []).some(
        child => String(child.id) === String(childMMRef.id)
      );
      if (!alreadyInTarget) {
        targetMMRef.addChild(childMMRef);
      } else {
        childMMRef.parent = targetMMRef;
      }

      const cyChildNode = cy.getElementById(childMMRef.id);
      if (cyChildNode.length > 0) {
        if (cyChildNode.data('parent') !== targetNodeId) {
          cyChildNode.move({ parent: targetNodeId });
        }
        const childLabel = childMMRef.label || cyChildNode.data('label') || '';
        const childDisplayLabel = computeDisplayLabel(childLabel, childMMRef);
        cyChildNode.data({
          parent: targetNodeId,
          display: 'element',
          label: childLabel,
          displayLabel: childDisplayLabel,
          codeName: childMMRef.codeName || '',
          childGroupType: childMMRef.childGroupType || ChildGroupType.None,
        });
        cyChildNode.style('label', childDisplayLabel);
        applyAutoLabelWidth(cyChildNode, childDisplayLabel);
      }
    });

    // 将目标父节点下已有的 state/value 子节点带入当前图层（含历史已关联但当前图层未展示的子节点）。
    const targetStateValueChildren = (targetMMRef.children || []).filter(
      child =>
        !child.deleted &&
        (child.type === NodeType.State || child.type === NodeType.Value)
    );
    const linkedPosition =
      linkedNode.length > 0 ? linkedNode.position() : oldPosition;
    targetStateValueChildren.forEach(childMMRef => {
      let cyChildNode = cy.getElementById(childMMRef.id);
      if (cyChildNode.length === 0) {
        addNode(
          cy,
          {
            id: childMMRef.id,
            label: childMMRef.label || childMMRef.type,
            type: childMMRef.type as NodeType,
            parent: targetNodeId,
            MMRef: childMMRef,
          },
          {
            x: linkedPosition.x,
            y: linkedPosition.y,
          },
          targetMMRef
        );
        cyChildNode = cy.getElementById(childMMRef.id);
      }

      if (cyChildNode.length > 0) {
        if (cyChildNode.data('parent') !== targetNodeId) {
          cyChildNode.move({ parent: targetNodeId });
        }
        const childLabel = childMMRef.label || cyChildNode.data('label') || '';
        const childDisplayLabel = computeDisplayLabel(childLabel, childMMRef);
        cyChildNode.data({
          parent: targetNodeId,
          display: 'element',
          label: childLabel,
          displayLabel: childDisplayLabel,
          codeName: childMMRef.codeName || '',
          childGroupType: childMMRef.childGroupType || ChildGroupType.None,
        });
        cyChildNode.style('label', childDisplayLabel);
        applyAutoLabelWidth(cyChildNode, childDisplayLabel);
      }
    });

    // 若目标父节点尚未确定分组类型，则继承源父节点的 state/value 分组。
    const hasStateChild = targetStateValueChildren.some(
      child => child.type === NodeType.State
    );
    const hasValueChild = targetStateValueChildren.some(
      child => child.type === NodeType.Value
    );
    if (hasStateChild) {
      targetMMRef.childGroupType = ChildGroupType.State;
    } else if (hasValueChild) {
      targetMMRef.childGroupType = ChildGroupType.Value;
    } else if (
      targetMMRef.childGroupType === ChildGroupType.None &&
      (sourceMMRef.childGroupType === ChildGroupType.State ||
        sourceMMRef.childGroupType === ChildGroupType.Value)
    ) {
      targetMMRef.childGroupType = sourceMMRef.childGroupType;
    }
    if (linkedNode.length > 0) {
      linkedNode.data('childGroupType', targetMMRef.childGroupType);
    }

    // 仅在当前图层重定向连线端点到目标节点，避免“连线动态级联”。
    connectedEdges.forEach(edge => {
      const sourceId = edge.data('source');
      const targetId = edge.data('target');
      const nextSourceId = sourceId === sourceNodeId ? targetNodeId : sourceId;
      const nextTargetId = targetId === sourceNodeId ? targetNodeId : targetId;

      edge.move({
        source: nextSourceId,
        target: nextTargetId,
      });
      edge.data({
        source: nextSourceId,
        target: nextTargetId,
      });

      const edgeMMRef = edge.data('MMRef') as MMEdge | null;
      if (edgeMMRef) {
        if (edgeMMRef.source?.id === sourceNodeId) {
          edgeMMRef.source = targetMMRef;
        }
        if (edgeMMRef.target?.id === sourceNodeId) {
          edgeMMRef.target = targetMMRef;
        }
      }
    });

    oldParentMMRef?.removeChild?.(sourceMMRef);
    sourceMMRef.deleted = true;
    activeNode.data({ MMRef: null });
    activeNode.remove();

    setName(targetMMRef.label || '');
    setCodeName(targetMMRef.codeName || '');
    setDescription(targetMMRef.description || '');
    setTags(Array.isArray(targetMMRef.tags) ? targetMMRef.tags : []);
    setExampleValues(targetMMRef.exampleValues || '');
    setNameError(null);
    (window as any).__opmcvNodeEditorBlockClearSelection = false;
    closeAssociateModal();
    message.success('关联成功，当前图层已复用已有节点');
  };

  const handleSaveName = (nextName = name) => {
    // 关联弹窗打开时，忽略名称输入框 blur 触发的重复校验，避免弹窗内操作反复弹 warning。
    if (associateModalVisible) {
      return false;
    }
    const trimmed = (nextName || '').trim();
    if (!trimmed) {
      message.warning('名称为必填项');
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => nameInputRef.current?.focus?.(), 0);
      return false;
    }
    if (trimmed.length < MIN_NAME_LENGTH) {
      const errMsg = `名称需为 ${MIN_NAME_LENGTH}-${MAX_NAME_LENGTH} 个字符`;
      message.warning(errMsg);
      setNameError(errMsg);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => nameInputRef.current?.focus?.(), 0);
      return false;
    }
    if (trimmed.length > MAX_NAME_LENGTH) {
      message.warning(`名称最多 ${MAX_NAME_LENGTH} 个字符`);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => nameInputRef.current?.focus?.(), 0);
      return false;
    }
    if (!NAME_VALID_PATTERN.test(trimmed)) {
      const errMsg =
        '名称只能包含中文、字母、数字、下划线，且不能以下划线或数字开头';
      message.warning(errMsg);
      setNameError(errMsg);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => nameInputRef.current?.focus?.(), 0);
      return false;
    }
    setNameError(null);
    setName(trimmed);
    (window as any).__opmcvNodeEditorBlockClearSelection = false;
    const nodeId = editingNodeIdRef.current || currentNode?.id();
    if (!cy || !nodeId) return false;
    const node = cy.getElementById(nodeId);
    if (!node || node.length === 0) return false;

    // 名称变更时，执行跨图层全局重名检查（兼容历史数据：仅拦截本次编辑）。
    const currentLabel = (node.data('label') || '').trim();
    const currentNodeType = (node.data('type') ||
      node.data('MMRef')?.type ||
      '') as string;
    const shouldCheckCrossLayerDuplicate =
      currentNodeType === NodeType.Object ||
      (currentNodeType === NodeType.Process && enableProcessNodeCascade);
    const isStateOrValueNode =
      currentNodeType === NodeType.State || currentNodeType === NodeType.Value;

    // state/value 仅在同父节点范围内校验重名，不参与跨图层关联。
    if (isStateOrValueNode && trimmed !== currentLabel) {
      const nodeMMRef = node.data('MMRef') as MMNode | null;
      const parentMMRef =
        ((node.parent()?.length
          ? node.parent().data('MMRef')
          : null) as MMNode | null) ||
        ((nodeMMRef?.parent as MMNode | MMRoot | null) instanceof MMNode
          ? (nodeMMRef?.parent as MMNode)
          : null);

      if (parentMMRef) {
        const duplicatedInParent = (parentMMRef.children || []).some(child => {
          if (!child || child.deleted) return false;
          if (child.type !== NodeType.State && child.type !== NodeType.Value) {
            return false;
          }
          if (String(child.id) === String(nodeId)) return false;
          return (child.label || '').trim() === trimmed;
        });

        if (duplicatedInParent) {
          const errMsg = '该名称在当前父节点下已存在，请重新命名';
          message.warning(errMsg);
          setNameError(errMsg);
          closeAssociateModal();
          (window as any).__opmcvNodeEditorBlockClearSelection = true;
          setTimeout(() => nameInputRef.current?.focus?.(), 0);
          return false;
        }
      }
    }

    if (
      shouldCheckCrossLayerDuplicate &&
      trimmed !== currentLabel &&
      hasDuplicateLabelAcrossLayers(trimmed, nodeId)
    ) {
      const duplicates = findDuplicateLabelCandidates(trimmed, nodeId);
      const currentLayerNodeIds = getCurrentDiagramNodeIdSet();
      const effectiveDuplicates = duplicates.filter(item => {
        const duplicateId = String(item.id);
        return (
          currentLayerNodeIds.has(duplicateId) ||
          countNodeDiagramAppearances(duplicateId) > 0
        );
      });

      const currentLayerDuplicates = effectiveDuplicates.filter(item =>
        currentLayerNodeIds.has(String(item.id))
      );
      if (currentLayerDuplicates.length > 0) {
        const errMsg = '该名称在当前图层已存在，请重新命名';
        message.warning(errMsg);
        setNameError(errMsg);
        closeAssociateModal();
        (window as any).__opmcvNodeEditorBlockClearSelection = true;
        setTimeout(() => nameInputRef.current?.focus?.(), 0);
        return false;
      }

      const crossLayerDuplicates = effectiveDuplicates.filter(
        item => !currentLayerNodeIds.has(String(item.id))
      );
      if (crossLayerDuplicates.length > 0) {
        const candidates: DuplicateLabelCandidate[] = crossLayerDuplicates.map(
          item => ({
            id: String(item.id),
            label: item.label || '',
            codeName: item.codeName || '',
            diagramLabels: getNodeDiagramLabels(String(item.id)),
            mmRef: item,
          })
        );

        const errMsg = '该名称在其他图层已存在，请重新命名或改为关联已有节点';
        message.warning(errMsg);
        setNameError(errMsg);
        setAssociateCandidates(candidates);
        setSelectedAssociateId(candidates[0]?.id || '');
        setAssociateModalVisible(candidates.length > 0);
        (window as any).__opmcvNodeEditorBlockClearSelection = true;
        return false;
      }
    }

    // state/value 不参与跨图层同名关联，若此前误开了弹窗则关闭。
    if (!shouldCheckCrossLayerDuplicate) {
      closeAssociateModal();
    }

    const beforeLabel = node.data('label') || '';
    node.data({ label: trimmed });
    const mmRef = node.data('MMRef');
    if (mmRef) {
      if (mmRef.label !== trimmed) {
        ensureNodeSnapshot(mmRef);
        markNodeDirty(mmRef);
      }
      mmRef.label = trimmed;
      const displayLabel = computeDisplayLabel(trimmed, mmRef);
      node.data('displayLabel', displayLabel);
      node.style('label', displayLabel);
      applyAutoLabelWidth(node, displayLabel);
    } else {
      applyAutoLabelWidth(node, trimmed);
    }
    if (beforeLabel !== trimmed && mmRef) {
      ensureNodeSnapshot(mmRef);
      markNodeDirty(mmRef);
      if (mmRef.diagram) {
        dispatch({ type: ACTIONS.UPDATE_TREE });
      }
    }
    return true;
  };

  const handleSavePackageName = (nextPackageName = packageName) => {
    const node = getActiveNode();
    if (!node) return false;
    const mmRef = node.data('MMRef') as MMNode | null;
    if (!mmRef || !isTopLevelNode(mmRef)) {
      return false;
    }

    const rawValue = (nextPackageName || '').trim();
    if (rawValue && !isPackageNameValid(rawValue)) {
      const errMsg = '包名仅支持小写英文和 `.`，示例：com.example';
      message.warning(errMsg);
      setPackageNameError(errMsg);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => packageNameInputRef.current?.focus?.(), 0);
      return false;
    }

    const normalized = normalizePackageName(rawValue);
    setPackageNameError(null);
    setPackageName(normalized);
    (window as any).__opmcvNodeEditorBlockClearSelection = false;

    const before = mmRef.packageName || '';
    if (before === normalized) {
      setNamespace(mmRef.namespace || currentNode?.data('namespace') || '');
      return true;
    }

    ensureNodeSnapshot(mmRef);
    markNodeDirty(mmRef);
    mmRef.packageName = normalized;
    recalculateNamespacesForMasterModel();
    updateFromMasterModel();
    setNamespace(mmRef.namespace || '');
    dispatch({ type: ACTIONS.UPDATE_TREE });
    return true;
  };

  const handleSaveCodeName = (nextCodeName = codeName) => {
    const trimmed = (nextCodeName || '').trim();
    if (trimmed && trimmed.length < MIN_CODE_NAME_LENGTH) {
      const errMsg = `英文名称需为 ${MIN_CODE_NAME_LENGTH}-${MAX_CODE_NAME_LENGTH} 个字符`;
      message.warning(errMsg);
      setCodeNameError(errMsg);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => codeNameInputRef.current?.focus?.(), 0);
      return false;
    }
    if (trimmed.length > MAX_CODE_NAME_LENGTH) {
      message.warning(`英文名称最多 ${MAX_CODE_NAME_LENGTH} 个字符`);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => codeNameInputRef.current?.focus?.(), 0);
      return false;
    }
    if (trimmed && !CODE_NAME_VALID_PATTERN.test(trimmed)) {
      const errMsg = '英文名称只能包含字母、数字、下划线，且必须以字母开头';
      message.warning(errMsg);
      setCodeNameError(errMsg);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => codeNameInputRef.current?.focus?.(), 0);
      return false;
    }
    setCodeNameError(null);
    setCodeName(trimmed);
    (window as any).__opmcvNodeEditorBlockClearSelection = false;
    const node = getActiveNode();
    if (!node) return false;
    const nodeId = node.id();
    const currentNodeType = (node.data('type') ||
      node.data('MMRef')?.type ||
      '') as string;
    const shouldCheckCrossLayerCodeNameDuplicate =
      currentNodeType === NodeType.Object ||
      (currentNodeType === NodeType.Process && enableProcessNodeCascade);

    // 英文名称同样要求跨图层全局唯一。
    if (
      shouldCheckCrossLayerCodeNameDuplicate &&
      hasDuplicateCodeNameAcrossLayers(trimmed, nodeId)
    ) {
      const errMsg = '英文名称在其他图层已存在，请使用其他英文名称';
      message.warning(errMsg);
      setCodeNameError(errMsg);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => codeNameInputRef.current?.focus?.(), 0);
      return false;
    }

    const mmRef = node.data('MMRef');
    const before = mmRef?.codeName || node.data('codeName') || '';
    node.data({ codeName: trimmed });
    if (mmRef) {
      if (before !== trimmed) {
        ensureNodeSnapshot(mmRef);
        markNodeDirty(mmRef);
      }
      mmRef.codeName = trimmed;
    }
    return true;
  };

  const handleSaveDescription = (nextDescription = description) => {
    const normalized = nextDescription || '';
    if (normalized.length > MAX_DESCRIPTION_LENGTH) {
      message.warning(`描述最多 ${MAX_DESCRIPTION_LENGTH} 个字符`);
      (window as any).__opmcvNodeEditorBlockClearSelection = true;
      setTimeout(() => descriptionInputRef.current?.focus?.(), 0);
      return false;
    }
    (window as any).__opmcvNodeEditorBlockClearSelection = false;
    const node = getActiveNode();
    if (!node) return false;
    const before = node.data('description') || '';
    node.data({ description: normalized });
    const mmRef = node.data('MMRef');
    if (mmRef) {
      if (mmRef.description !== normalized) {
        ensureNodeSnapshot(mmRef);
        markNodeDirty(mmRef);
      }
      mmRef.description = normalized;
    }
    if (before !== normalized && mmRef) {
      ensureNodeSnapshot(mmRef);
      markNodeDirty(mmRef);
    }
    return true;
  };

  const handleTagsChange = (nextTags: string[]) => {
    const node = getActiveNode();
    if (!node) return;
    const mmRef = node.data('MMRef');
    const normalized = Array.from(
      new Set(nextTags.map(item => item.trim()))
    ).filter(Boolean);
    const before = Array.isArray(mmRef?.tags) ? mmRef.tags : [];
    if (
      before.length === normalized.length &&
      before.every((item: string, idx: number) => item === normalized[idx])
    ) {
      return;
    }
    if (mmRef) {
      ensureNodeSnapshot(mmRef);
      markNodeDirty(mmRef);
      mmRef.tags = normalized;
    }
    node.data({ tags: normalized });
    setTags(normalized);
  };

  const handleAddTag = () => {
    const next = (tagInput || '').trim();
    if (!next) return;
    if (tags.length >= MAX_TAGS) {
      message.warning(`最多添加 ${MAX_TAGS} 个标签`);
      setTagInput('');
      return;
    }
    if (next.length > MAX_TAG_LENGTH) {
      message.warning(`标签最多${MAX_TAG_LENGTH}个字符`);
      return;
    }
    const nextTags = [...tags, next];
    setTagInput('');
    handleTagsChange(nextTags);
  };

  const handleRemoveTag = (tag: string) => {
    const nextTags = tags.filter(item => item !== tag);
    handleTagsChange(nextTags);
  };

  const handleSaveExampleValues = (nextValue = exampleValues) => {
    const node = getActiveNode();
    if (!node) return;
    const mmRef = node.data('MMRef');
    const normalized = nextValue || '';
    const before = node.data('exampleValues') || '';
    if (before === normalized) return;
    if (mmRef) {
      ensureNodeSnapshot(mmRef);
      markNodeDirty(mmRef);
      mmRef.exampleValues = normalized;
    }
    node.data({ exampleValues: normalized });
    setExampleValues(normalized);
  };

  const handleSaveAutonomy = (nextAutonomy?: {
    action?: string;
    prompt?: string;
    withTool?: boolean;
  }) => {
    const node = getActiveNode();
    if (!node) return false;
    const mmRef = node.data('MMRef') as MMNode | null;
    if (!mmRef || mmRef.type !== NodeType.Process) return false;

    const normalized = {
      action: nextAutonomy?.action ?? autonomyAction,
      prompt: nextAutonomy?.prompt ?? autonomyPrompt,
      withTool: nextAutonomy?.withTool ?? autonomyWithTool,
    };
    const before = {
      action: mmRef.autonomy?.action || '',
      prompt: mmRef.autonomy?.prompt || '',
      withTool: !!mmRef.autonomy?.withTool,
    };

    if (
      before.action === normalized.action &&
      before.prompt === normalized.prompt &&
      before.withTool === normalized.withTool
    ) {
      return true;
    }

    ensureNodeSnapshot(mmRef);
    markNodeDirty(mmRef);
    mmRef.autonomy = normalized;
    node.data({ autonomy: normalized });
    setAutonomyAction(normalized.action);
    setAutonomyPrompt(normalized.prompt);
    setAutonomyWithTool(normalized.withTool);
    dispatch({ type: ACTIONS.UPDATE_TREE });
    return true;
  };

  const getNodeDisplayLabel = (node: any) =>
    node?.data?.('displayLabel') ||
    node?.data?.('label') ||
    node?.data?.('name') ||
    node?.id?.() ||
    '';

  const getParameterDisplayLabel = (node: any) => {
    if (!node) return '';
    const nodeData = node.data ? node.data() : {};
    if (nodeData.type === 'state') {
      const parentNode = node.parent?.();
      if (parentNode && parentNode.length) {
        return getNodeDisplayLabel(parentNode);
      }
    }
    return getNodeDisplayLabel(node);
  };

  const parentObjects = useMemo(() => {
    if (!currentNode) return [];
    const result: string[] = [];
    currentNode.connectedEdges().forEach(edge => {
      if (edge.data('type') !== EdgeType.Generalization) return;
      if (edge.target().id() === currentNode.id()) {
        result.push(getNodeDisplayLabel(edge.source()));
      }
    });
    return Array.from(new Set(result));
  }, [currentNode]);

  const childObjects = useMemo(() => {
    if (!currentNode) return [];
    const result: string[] = [];
    currentNode.connectedEdges().forEach(edge => {
      if (edge.data('type') !== EdgeType.Generalization) return;
      if (edge.source().id() === currentNode.id()) {
        result.push(getNodeDisplayLabel(edge.target()));
      }
    });
    return Array.from(new Set(result));
  }, [currentNode]);

  const collectAncestorObjects = () => {
    if (!currentNode) return [];
    const visited = new Set<string>();
    const result: any[] = [];
    const stack: any[] = [currentNode];
    visited.add(currentNode.id());
    while (stack.length) {
      const node = stack.pop();
      node.connectedEdges().forEach((edge: any) => {
        if (edge.data('type') !== EdgeType.Generalization) return;
        if (edge.target().id() !== node.id()) return;
        const parent = edge.source();
        const parentId = parent?.id?.();
        if (!parentId || visited.has(parentId)) return;
        visited.add(parentId);
        result.push(parent);
        stack.push(parent);
      });
    }
    return result;
  };

  const exhibitionAttributes = useMemo(() => {
    if (!currentNode) return [];
    const attrs: string[] = [];
    currentNode.connectedEdges().forEach(edge => {
      if (edge.data('type') !== EdgeType.Exhibition) return;
      if (edge.source().id() === currentNode.id()) {
        attrs.push(getNodeDisplayLabel(edge.target()));
      }
    });
    return attrs;
  }, [currentNode]);

  const defaultAttributes = useMemo(() => {
    if (!currentNode) return [];
    const children = currentNode.children('[type = "state"]') || [];
    return children.map((child: any) => getNodeDisplayLabel(child));
  }, [currentNode]);

  const inheritedAttributes = useMemo(() => {
    if (!currentNode) return [];
    const ancestors = collectAncestorObjects();
    const attrs: string[] = [];
    ancestors.forEach(node => {
      node.connectedEdges().forEach((edge: any) => {
        if (edge.data('type') !== EdgeType.Exhibition) return;
        if (edge.source().id() !== node.id()) return;
        attrs.push(getNodeDisplayLabel(edge.target()));
      });
      const children = node.children('[type = "state"]') || [];
      children.forEach((child: any) => {
        attrs.push(getNodeDisplayLabel(child));
      });
    });
    return attrs;
  }, [currentNode]);

  const relationStats = useMemo(() => {
    const stats = {
      exhibition: 0,
      conversion: 0,
      agent: 0,
      instrument: 0,
      invocation: 0,
    };
    if (!currentNode) return stats;
    currentNode.connectedEdges().forEach(edge => {
      const type = edge.data('type');
      const source = edge.source();
      const target = edge.target();
      if (type === EdgeType.Exhibition) {
        if (
          source?.data?.('type') === 'object' &&
          target?.data?.('type') === 'object'
        ) {
          stats.exhibition += 1;
        }
      } else if (type === EdgeType.Agent) {
        stats.agent += 1;
      } else if (type === EdgeType.Instrument) {
        stats.instrument += 1;
      } else if (
        type === EdgeType.Consumption ||
        type === EdgeType.Generation ||
        type === EdgeType.Effect
      ) {
        stats.conversion += 1;
      } else if (type === EdgeType.Invocation) {
        stats.invocation += 1;
      }
    });
    return stats;
  }, [currentNode]);

  const inputParameters = useMemo(() => {
    if (!currentNode || !isProcessNode) return [];
    const result: string[] = [];
    currentNode.connectedEdges().forEach(edge => {
      const edgeType = edge.data('type');
      if (
        edgeType !== EdgeType.Agent &&
        edgeType !== EdgeType.Instrument &&
        edgeType !== EdgeType.Consumption
      ) {
        return;
      }
      if (edge.target().id() === currentNode.id()) {
        result.push(getParameterDisplayLabel(edge.source()));
      }
    });
    return Array.from(new Set(result.filter(Boolean)));
  }, [currentNode, isProcessNode]);

  const outputParameters = useMemo(() => {
    if (!currentNode || !isProcessNode) return [];
    const result: string[] = [];
    currentNode.connectedEdges().forEach(edge => {
      if (edge.data('type') !== EdgeType.Generation) return;
      if (edge.source().id() === currentNode.id()) {
        result.push(getParameterDisplayLabel(edge.target()));
      }
    });
    return Array.from(new Set(result.filter(Boolean)));
  }, [currentNode, isProcessNode]);

  const displayRelatedSystem = currentMMRef?.relatedSystem || '';

  const nodePositionText = useMemo(() => {
    if (!currentNode) return '-';
    const pos = currentNode.position();
    const x = Math.round(pos.x || 0);
    const y = Math.round(pos.y || 0);
    return `(${x}, ${y})`;
  }, [currentNode]);

  const processTypeText = useMemo(() => {
    if (!processDefinition?.language) return '-';
    const language =
      processDefinition.language === 'PYTHON' ? 'Python' : 'TypeScript';
    return `用户定义函数（${language}）`;
  }, [processDefinition]);

  const runtimeConfigText = useMemo(() => {
    if (!processDefinition) return '-';

    // 与新 function 接口字段保持一致：属性面板当前仅展示超时时间与内存限制。
    const runtimeParts = [
      `超时: ${formatRuntimeLimitText(processDefinition.timeoutSeconds, '秒')}`,
      `内存: ${formatRuntimeLimitText(processDefinition.maxMemoryMb, 'MB')}`,
    ];

    return runtimeParts.join('，');
  }, [processDefinition]);

  const renderChipList = (
    items: string[],
    emptyText = '-',
    colorClass = 'text-gray-600'
  ) => {
    if (!items.length) {
      return <span className="text-xs text-gray-400">{emptyText}</span>;
    }
    return (
      <div className="flex flex-wrap gap-2">
        {items.map((item, index) => (
          <span
            key={`${item}-${index}`}
            className={`pr-2 py-0.5 text-xs border rounded ${colorClass}`}
          >
            {item}
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <div
        className={`node-editor-sidebar ${visible ? 'visible' : ''} text-left`}
        style={{ right: offsetRight, width }}
      >
        <div className="node-editor-sidebar-header">
          <h3>属性面板</h3>
          <button
            className="node-editor-sidebar-close"
            onClick={handleSidebarClose}
            title="关闭"
          >
            ×
          </button>
        </div>

        <div className="node-editor-sidebar-content text-left">
          {currentNode ? (
            <div className="flex flex-col gap-6">
              {isObjectNode && (
                <ObjectNodePropertyPanel
                  nodeId={nodeIdText}
                  parentNodeId={parentNodeIdText}
                  name={name}
                  nameError={nameError}
                  codeName={codeName}
                  codeNameError={codeNameError}
                  description={description}
                  namespace={namespace}
                  packageName={packageName}
                  packageNameError={packageNameError}
                  packageNameDisabled={!isTopLevelPackageEditable}
                  tags={tags}
                  tagInput={tagInput}
                  exampleValues={exampleValues}
                  parentObjects={parentObjects}
                  childObjects={childObjects}
                  exhibitionAttributes={exhibitionAttributes}
                  defaultAttributes={defaultAttributes}
                  inheritedAttributes={inheritedAttributes}
                  relationStats={relationStats}
                  relatedSystem={displayRelatedSystem}
                  nodePositionText={nodePositionText}
                  nameInputRef={nameInputRef}
                  codeNameInputRef={codeNameInputRef}
                  descriptionInputRef={descriptionInputRef}
                  packageNameInputRef={packageNameInputRef}
                  onNameChange={value => {
                    setName(value);
                    if (nameError) setNameError(null);
                  }}
                  onNameFocus={() => {
                    editingNodeIdRef.current = currentNode?.id() || null;
                  }}
                  onNameBlur={value => {
                    const ok = handleSaveName(value);
                    if (ok) {
                      editingNodeIdRef.current = null;
                    }
                  }}
                  onCodeNameChange={value => {
                    setCodeName(value);
                    if (codeNameError) setCodeNameError(null);
                  }}
                  onCodeNameFocus={() => {
                    editingNodeIdRef.current = currentNode?.id() || null;
                  }}
                  onCodeNameBlur={value => {
                    const ok = handleSaveCodeName(value);
                    if (ok) {
                      editingNodeIdRef.current = null;
                    }
                  }}
                  onDescriptionChange={value => setDescription(value)}
                  onDescriptionFocus={() => {
                    editingNodeIdRef.current = currentNode?.id() || null;
                  }}
                  onDescriptionBlur={value => {
                    const ok = handleSaveDescription(value);
                    if (ok) {
                      editingNodeIdRef.current = null;
                    }
                  }}
                  onPackageNameChange={value => {
                    setPackageName(value);
                    if (packageNameError) setPackageNameError(null);
                  }}
                  onPackageNameFocus={() => {
                    editingNodeIdRef.current = currentNode?.id() || null;
                  }}
                  onPackageNameBlur={value => {
                    const ok = handleSavePackageName(value);
                    if (ok) {
                      editingNodeIdRef.current = null;
                    }
                  }}
                  onTagInputChange={value => setTagInput(value)}
                  onAddTag={handleAddTag}
                  onRemoveTag={handleRemoveTag}
                  onExampleValuesChange={value => setExampleValues(value)}
                  onExampleValuesFocus={() => {
                    editingNodeIdRef.current = currentNode?.id() || null;
                  }}
                  onExampleValuesBlur={value => {
                    handleSaveExampleValues(value);
                    editingNodeIdRef.current = null;
                  }}
                  renderChipList={renderChipList}
                />
              )}

              {isProcessNode && (
                <Spin spinning={loadingProcessDefinition}>
                  <ProcessNodePropertyPanel
                    nodeId={nodeIdText}
                    parentNodeId={parentNodeIdText}
                    name={name}
                    nameError={nameError}
                    codeName={codeName}
                    codeNameError={codeNameError}
                    description={description}
                    namespace={namespace}
                    packageName={packageName}
                    packageNameError={packageNameError}
                    packageNameDisabled={!isTopLevelPackageEditable}
                    tags={tags}
                    tagInput={tagInput}
                    autonomyAction={autonomyAction}
                    autonomyPrompt={autonomyPrompt}
                    autonomyWithTool={autonomyWithTool}
                    inputParameters={inputParameters}
                    outputParameters={outputParameters}
                    processTypeText={processTypeText}
                    runtimeConfigText={runtimeConfigText}
                    nameInputRef={nameInputRef}
                    codeNameInputRef={codeNameInputRef}
                    descriptionInputRef={descriptionInputRef}
                    packageNameInputRef={packageNameInputRef}
                    onNameChange={value => {
                      setName(value);
                      if (nameError) setNameError(null);
                    }}
                    onNameFocus={() => {
                      editingNodeIdRef.current = currentNode?.id() || null;
                    }}
                    onNameBlur={value => {
                      const ok = handleSaveName(value);
                      if (ok) {
                        editingNodeIdRef.current = null;
                      }
                    }}
                    onCodeNameChange={value => {
                      setCodeName(value);
                      if (codeNameError) setCodeNameError(null);
                    }}
                    onCodeNameFocus={() => {
                      editingNodeIdRef.current = currentNode?.id() || null;
                    }}
                    onCodeNameBlur={value => {
                      const ok = handleSaveCodeName(value);
                      if (ok) {
                        editingNodeIdRef.current = null;
                      }
                    }}
                    onDescriptionChange={value => setDescription(value)}
                    onDescriptionFocus={() => {
                      editingNodeIdRef.current = currentNode?.id() || null;
                    }}
                    onDescriptionBlur={value => {
                      const ok = handleSaveDescription(value);
                      if (ok) {
                        editingNodeIdRef.current = null;
                      }
                    }}
                    onPackageNameChange={value => {
                      setPackageName(value);
                      if (packageNameError) setPackageNameError(null);
                    }}
                    onPackageNameFocus={() => {
                      editingNodeIdRef.current = currentNode?.id() || null;
                    }}
                    onPackageNameBlur={value => {
                      const ok = handleSavePackageName(value);
                      if (ok) {
                        editingNodeIdRef.current = null;
                      }
                    }}
                    onTagInputChange={value => setTagInput(value)}
                    onAddTag={handleAddTag}
                    onRemoveTag={handleRemoveTag}
                    onAutonomyActionChange={value => setAutonomyAction(value)}
                    onAutonomyActionFocus={() => {
                      editingNodeIdRef.current = currentNode?.id() || null;
                    }}
                    onAutonomyActionBlur={value => {
                      const ok = handleSaveAutonomy({ action: value });
                      if (ok) {
                        editingNodeIdRef.current = null;
                      }
                    }}
                    onAutonomyPromptChange={value => setAutonomyPrompt(value)}
                    onAutonomyPromptFocus={() => {
                      editingNodeIdRef.current = currentNode?.id() || null;
                    }}
                    onAutonomyPromptBlur={value => {
                      const ok = handleSaveAutonomy({ prompt: value });
                      if (ok) {
                        editingNodeIdRef.current = null;
                      }
                    }}
                    onAutonomyWithToolChange={checked => {
                      setAutonomyWithTool(checked);
                      handleSaveAutonomy({ withTool: checked });
                    }}
                    onOpenProcessDefinition={() => {
                      if (currentNode?.id()) {
                        onOpenProcessDefinition(currentNode.id());
                      }
                    }}
                    renderChipList={renderChipList}
                  />
                </Spin>
              )}

              {isStateOrValueNode && (
                <SectionBlock title="基本信息">
                  <div className="flex flex-col gap-2">
                    <FieldBlock label="命名空间">
                      <ReadonlyTextValue value={namespace} />
                    </FieldBlock>

                    <FieldBlock label="包名">
                      <div className="flex flex-col gap-1">
                        <Input
                          ref={packageNameInputRef}
                          value={packageName}
                          placeholder={
                            !isTopLevelPackageEditable
                              ? DISABLED_PACKAGE_NAME_PLACEHOLDER
                              : DEFAULT_PACKAGE_NAME_PLACEHOLDER
                          }
                          title={
                            !isTopLevelPackageEditable
                              ? DISABLED_PACKAGE_NAME_TITLE
                              : undefined
                          }
                          disabled={!isTopLevelPackageEditable}
                          status={packageNameError ? 'error' : undefined}
                          onChange={e => {
                            setPackageName(e.target.value);
                            if (packageNameError) setPackageNameError(null);
                          }}
                          onFocus={() => {
                            editingNodeIdRef.current =
                              currentNode?.id() || null;
                          }}
                          onBlur={e => {
                            const ok = handleSavePackageName(e.target.value);
                            if (ok) {
                              editingNodeIdRef.current = null;
                            }
                          }}
                          onPressEnter={e =>
                            (e.target as HTMLInputElement).blur()
                          }
                        />
                        {packageNameError && (
                          <div className="text-xs text-red-500">
                            {packageNameError}
                          </div>
                        )}
                      </div>
                    </FieldBlock>

                    <FieldBlock label="节点ID">
                      <ReadonlyTextValue value={nodeIdText} />
                    </FieldBlock>
                  </div>

                  <FieldBlock label="名称">
                    <div className="flex flex-col gap-1">
                      <Input
                        ref={nameInputRef}
                        value={name}
                        placeholder="请输入名称"
                        maxLength={MAX_NAME_LENGTH}
                        showCount
                        status={nameError ? 'error' : undefined}
                        onChange={e => {
                          setName(e.target.value);
                          if (nameError) setNameError(null);
                        }}
                        onFocus={() => {
                          editingNodeIdRef.current = currentNode?.id() || null;
                        }}
                        onBlur={e => {
                          const ok = handleSaveName(e.target.value);
                          if (ok) {
                            editingNodeIdRef.current = null;
                          }
                        }}
                        onPressEnter={e =>
                          (e.target as HTMLInputElement).blur()
                        }
                      />
                      {nameError && (
                        <div className="text-xs text-red-500">{nameError}</div>
                      )}
                    </div>
                  </FieldBlock>

                  <FieldBlock label="描述">
                    <Input.TextArea
                      ref={descriptionInputRef}
                      value={description}
                      placeholder="请输入描述（可选）"
                      rows={4}
                      maxLength={MAX_DESCRIPTION_LENGTH}
                      showCount
                      onChange={e => setDescription(e.target.value)}
                      onFocus={() => {
                        editingNodeIdRef.current = currentNode?.id() || null;
                      }}
                      onBlur={e => {
                        const ok = handleSaveDescription(e.target.value);
                        if (ok) {
                          editingNodeIdRef.current = null;
                        }
                      }}
                    />
                  </FieldBlock>
                </SectionBlock>
              )}
            </div>
          ) : (
            <div className="empty-state">请选择一个节点进行编辑</div>
          )}
        </div>
      </div>

      <Modal
        open={associateModalVisible}
        title="关联已有节点"
        onCancel={handleCancelAssociateModal}
        onOk={handleAssociateNode}
        okText="确认关联"
        cancelText="取消"
        destroyOnClose
      >
        <div className="flex flex-col gap-3 text-sm text-gray-700">
          <div>
            发现同名节点，请选择要关联的已有节点。关联后将复用该节点的
            <span className="font-medium"> id / 名称 / 属性</span>
            ，并与其保持跨图层级联。
          </div>
          <Select
            value={selectedAssociateId || undefined}
            placeholder="请选择要关联的节点"
            onChange={value => setSelectedAssociateId(value)}
            options={associateCandidates.map(item => ({
              value: item.id,
              label: `${item.label}（ID: ${item.id}${item.codeName ? `，英文名: ${item.codeName}` : ''}${
                item.diagramLabels.length
                  ? `，图层: ${item.diagramLabels.join(' / ')}`
                  : ''
              }）`,
            }))}
          />
        </div>
      </Modal>
    </>
  );
};

export default NodePropertySidebar;
